<template>
    <div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Allowed in following countries</p>
            <multi-reference
                create-text="Create new country and link country"
                link-text="Link existing countries"
                :creatable="false"
                :language="language"
                v-model="value.allowed_countries"
                model-data="Country"
                :disabled="value.restricted_countries.length > 0"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
                :error="
                    errors[`contentBlocks.${contentBlockIndex}.allowed_countries`]
                        ? errors[`contentBlocks.${contentBlockIndex}.allowed_countries`][0]
                        : null
                "
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3 mb-3"
        >
            <p class="text-gray-700 mb-4">Restricted in following countries</p>
            <multi-reference
                create-text="Create new country and link country"
                link-text="Link existing countries"
                :creatable="false"
                :language="language"
                v-model="value.restricted_countries"
                :disabled="value.allowed_countries.length > 0"
                model-data="Country"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
                :error="
                    errors[`contentBlocks.${contentBlockIndex}.restricted_countries`]
                        ? errors[`contentBlocks.${contentBlockIndex}.restricted_countries`][0]
                        : null
                "
            />
        </div>

        <input-text
            :disabled="disabled"
            :id="`title_${contentBlockIndex}`"
            name="title"
            label="H2 Title"
            v-model="value.title"
            :error="
                errors[`contentBlocks.${contentBlockIndex}.title`]
                    ? errors[`contentBlocks.${contentBlockIndex}.title`][0]
                    : null
            "
            :maxlength=255
        />
        <input-text
            :disabled="disabled"
            :id="`anchor_${contentBlockIndex}`"
            name="anchor"
            label="Jump Id for H2 (excluding #)"
            v-model="value.anchor"
            class="mt-3"
            :error="
                errors[`contentBlocks.${contentBlockIndex}.anchor`]
                    ? errors[`contentBlocks.${contentBlockIndex}.anchor`][0]
                    : null
            "
            :maxlength=255
        />
        <input-text
            :disabled="disabled"
            name="anchor_text"
            label="Quick Navi Menu anchor text for Heading"
            class="mt-3"
            v-model="value.anchor_text"
            :error="errors.anchor_text ? errors.anchor_text[0] : null"
        />
        <content-section-list
            :disabled="disabled"
            :gallery="gallery"
            :language="language"
            :should-show-market-data="shouldShowMarketData"
            :additional-casinos-component="additionalCasinosComponent"
            :is-payment-category-page="isPaymentCategoryPage"
            :payment-method-id="paymentMethodId"
            v-model="value.sections"
            :key="`key_content_block_sections_${contentBlockIndex}`"
            :content-block-index="contentBlockIndex"
            :errors="errors"
            :show-all-content="showAllContent"
            :content-section-with-how-to="contentSectionWithHowTo"
            :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
            :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
            :content-sections-with-ranking="contentSectionsWithRanking"
            :document-class="documentClass"
            :document-id="documentId"
            :document-data-id="documentDataId"
            :document-type-id="documentTypeId"
            :has-bonus-lists="hasBonusLists"
            :has-ranking="hasRanking"
            @delete-content-section="deleteSection"
            @add-how-to="(uid) => $emit('add-how-to', uid)"
            @delete-how-to="(uid) => $emit('delete-how-to', uid)"
            @add-bonus-list-top="(uid) => $emit('add-bonus-list-top', uid)"
            @delete-bonus-list-top="(uid) => $emit('delete-bonus-list-top', uid)"
            @add-bonus-list-bottom="(uid) => $emit('add-bonus-list-bottom', uid)"
            @delete-bonus-list-bottom="(uid) => $emit('delete-bonus-list-bottom', uid)"
            @add-ranking="(uid) => $emit('add-ranking', uid)"
            @delete-ranking="(uid) => $emit('delete-ranking', uid)"
        />
        <button
            :disabled="disabled"
            type="button"
            :key="`key_content_block_sections_button_${contentBlockIndex}`"
            @click="$emit('add-content-section')"
            class="mt-2 mb-4"
            :class="disabled ? 'cursor-not-allowed' : null"
        >
            Add Element/Text under the Same H2
        </button>
    </div>
</template>

<script>
import siteLanguage from '../../config/siteLanguage'

export default {
    name: "ContentBlock",
    props: {
        gallery: Array,
        value: {
            type: Object,
            required: true,
        },
        contentBlockIndex: Number,
        errors: {
            type: [Array, Object],
            required: true,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        documentClass: {
            type: String,
            required: true,
        },
        documentId: {
            type: Number,
            required: false,
        },
        documentDataId: {
            type: Number,
            required: false,
        },
        documentTypeId: {
            type: Number,
            required: false,
        },
        hasBonusLists: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasRanking: {
            type: Boolean,
            required: false,
            default: true,
        },
        shouldShowMarketData: {
           type : Boolean,
           default : false
        },
        additionalCasinosComponent: {
            type: Object,
            default: () => {
                return { shouldShow: false };
            },
        },
        isPaymentCategoryPage: {
            type: Boolean,
            default: false,
        },
        paymentMethodId: {
            type: Number,
            required: false,
        },
        showAllContent: {
            type: Boolean,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        contentSectionWithHowTo: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListTop: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListBottom: {
            type: String,
            required: false,
        },
        contentSectionsWithRanking: {
            type: Array,
            required: false,
        },
    },
    methods: {
        deleteSection(sectionIndex) {
            this.$emit("delete-content-section", {
                contentBlockIndex: this.contentBlockIndex,
                sectionIndex,
            });
        }
    },
};
</script>
